import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { AlertMethod, Dealer, ToneOption } from "@/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const saveDealerToSupabase = async (dealer: Dealer): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from('dealers').insert([dealer]);
    
    if (error) {
      console.error("Supabase error:", error);
      return {
        success: false,
        error: error.message
      };
    }
    
    return {
      success: true
    };
  } catch (err) {
    console.error("Error saving dealer:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error"
    };
  }
};

const stepOneSchema = z.object({
  name: z.string().min(2, "Dealership name must be at least 2 characters"),
  salesperson_name: z.string().min(2, "Salesperson name must be at least 2 characters")
});
const stepTwoSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(10, "Phone number must be at least 10 digits")
});
const stepThreeSchema = z.object({
  preferred_tone: z.enum(["Friendly", "Professional", "Casual", "Urgent"] as const),
  custom_prompt: z.string().min(10, "Prompt must be at least 10 characters")
});
const stepFourSchema = z.object({
  alert_method: z.enum(["email", "sms", "both", "none"] as const)
});
const formSchema = stepOneSchema.merge(stepTwoSchema).merge(stepThreeSchema).merge(stepFourSchema);
type FormValues = z.infer<typeof formSchema>;
const steps = [{
  title: "Dealership Info",
  description: "Tell us about your dealership"
}, {
  title: "Contact Info",
  description: "How to reach you"
}, {
  title: "Voice Setup",
  description: "Configure the AI voice"
}, {
  title: "Notifications",
  description: "Get updates your way"
}];

const DealerOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      salesperson_name: "",
      email: "",
      phone: "",
      preferred_tone: "Friendly",
      custom_prompt: "",
      alert_method: "email"
    },
    mode: "onChange"
  });
  const isFirstStep = step === 0;
  const isLastStep = step === steps.length - 1;
  const validateCurrentStep = async () => {
    let isValid = false;
    switch (step) {
      case 0:
        isValid = await form.trigger(['name', 'salesperson_name']);
        break;
      case 1:
        isValid = await form.trigger(['email', 'phone']);
        break;
      case 2:
        isValid = await form.trigger(['preferred_tone', 'custom_prompt']);
        break;
      case 3:
        isValid = await form.trigger(['alert_method']);
        break;
    }
    return isValid;
  };
  const nextStep = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setStep(prev => prev + 1);
    }
  };
  const previousStep = () => {
    setStep(prev => prev - 1);
  };
  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      
      const dealer: Dealer = {
        name: data.name,
        salesperson_name: data.salesperson_name,
        email: data.email,
        phone: data.phone,
        preferred_tone: data.preferred_tone,
        custom_prompt: data.custom_prompt,
        alert_method: data.alert_method,
        created_at: new Date().toISOString()
      };
      
      const { success, error } = await saveDealerToSupabase(dealer);
      
      if (success) {
        toast({
          title: "Dealership onboarded successfully!",
          description: "You can now start uploading leads."
        });
        navigate("/leads/upload");
      } else {
        toast({
          title: "Failed to onboard dealership",
          description: error || "Please try again later",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error saving dealer:", error);
      toast({
        title: "Failed to onboard dealership",
        description: "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const currentErrors = form.formState.errors;
  const hasCurrentStepErrors = () => {
    switch (step) {
      case 0:
        return !!(currentErrors.name || currentErrors.salesperson_name);
      case 1:
        return !!(currentErrors.email || currentErrors.phone);
      case 2:
        return !!(currentErrors.preferred_tone || currentErrors.custom_prompt);
      case 3:
        return !!currentErrors.alert_method;
      default:
        return false;
    }
  };
  return <div className="max-w-2xl mx-auto">
      <Card className="airbnb-card">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold">Let's set up your Reheat account</CardTitle>
            <span className="text-sm text-gray-500">Step {step + 1} of {steps.length}</span>
          </div>
          <CardDescription>
            {steps[step].description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-8">
            <div className="flex justify-between">
              {steps.map((s, i) => <div key={i} className={`flex flex-col items-center ${i <= step ? 'text-primary' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full mb-1 transition-colors
                    ${i < step ? 'bg-primary text-white' : i === step ? 'border-2 border-primary' : 'border-2 border-gray-200'}`}>
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className="text-xs hidden md:inline">{s.title}</span>
                </div>)}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-0">
              <div className={`h-1 rounded-l ${step > 0 ? 'bg-primary' : 'bg-gray-200'}`}></div>
              <div className={`h-1 ${step > 1 ? 'bg-primary' : 'bg-gray-200'}`}></div>
              <div className={`h-1 rounded-r ${step > 2 ? 'bg-primary' : 'bg-gray-200'}`}></div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {step === 0 && <div className="space-y-4">
                  <FormField control={form.control} name="name" render={({
                field
              }) => <FormItem>
                        <FormLabel>Dealership Name</FormLabel>
                        <FormControl>
                          <Input placeholder="ABC Motors" className="airbnb-input" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <FormField control={form.control} name="salesperson_name" render={({
                field
              }) => <FormItem>
                        <FormLabel>Salesperson Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Smith" className="airbnb-input" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>}

              {step === 1 && <div className="space-y-4">
                  <FormField control={form.control} name="email" render={({
                field
              }) => <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="contact@abcmotors.com" className="airbnb-input" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <FormField control={form.control} name="phone" render={({
                field
              }) => <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input placeholder="+1 (555) 123-4567" className="airbnb-input" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>}

              {step === 2 && <div className="space-y-4">
                  <FormField control={form.control} name="preferred_tone" render={({
                field
              }) => <FormItem className="space-y-3">
                        <FormLabel>Preferred Tone</FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-wrap gap-4">
                            {["Friendly", "Professional", "Casual", "Urgent"].map(tone => <FormItem key={tone} className="flex items-center space-x-2 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value={tone} />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">{tone}</FormLabel>
                              </FormItem>)}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <FormField control={form.control} name="custom_prompt" render={({
                field
              }) => <FormItem>
                        <FormLabel>Custom Voice Prompt</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Hi, this is [salesperson] from [dealership]. I'm calling to follow up on your interest in purchasing a vehicle..." className="resize-none min-h-[100px] airbnb-input" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>}

              {step === 3 && <div className="space-y-4">
                  <FormField control={form.control} name="alert_method" render={({
                field
              }) => <FormItem className="space-y-3">
                        <FormLabel>Alert Method</FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-wrap gap-4">
                            {[{
                      value: "email",
                      label: "Email"
                    }, {
                      value: "sms",
                      label: "SMS"
                    }, {
                      value: "both",
                      label: "Both"
                    }, {
                      value: "none",
                      label: "None"
                    }].map(option => <FormItem key={option.value} className="flex items-center space-x-2 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value={option.value} />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">{option.label}</FormLabel>
                              </FormItem>)}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>}

              <div className="flex justify-between mt-8">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={previousStep} 
                  disabled={isFirstStep} 
                  className="airbnb-button gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                
                {isLastStep ? 
                  <Button 
                    type="submit" 
                    className="airbnb-button gap-2" 
                    disabled={hasCurrentStepErrors() || isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Complete"}
                    <Check className="h-4 w-4" />
                  </Button> : 
                  <Button 
                    type="button" 
                    onClick={nextStep} 
                    className="airbnb-button gap-2" 
                    disabled={hasCurrentStepErrors()}
                  >
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                }
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>;
};

export default DealerOnboarding;
